import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { divIcon } from 'leaflet';
import { useI18n } from '@/i18n';

export interface PolandCompanyMapPoint {
  id: string;
  kind: string;
  companyName: string;
  city: string;
  addressLine: string;
  postalCode: string;
  cityCode: string;
  locationLabel: string;
  lat: number;
  lon: number;
}

const polandCenter: [number, number] = [52.1, 19.4];

function createMarkerIcon(color: string) {
  return divIcon({
    className: 'varibox-map-marker',
    html: `<span style="display:block;width:27px;height:27px;background:${color};border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(15,23,42,.45);position:relative"><i style="position:absolute;left:7px;top:7px;width:9px;height:9px;background:#fff;border-radius:50%"></i></span>`,
    iconSize: [30, 38],
    iconAnchor: [15, 35],
    popupAnchor: [0, -32],
  });
}

const sublocationIcon = createMarkerIcon('#2563eb');
const clientNodeIcon = createMarkerIcon('#e11d48');

interface PolandCompanyMapProps {
  points: PolandCompanyMapPoint[];
  loading: boolean;
  emptyLabel: string;
  onPointSelect?: (point: PolandCompanyMapPoint) => void;
  onPointNavigate?: (point: PolandCompanyMapPoint) => void;
}

export default function PolandCompanyMap({ points, loading, emptyLabel, onPointSelect, onPointNavigate }: PolandCompanyMapProps) {
  const { t } = useI18n();
  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }} spacing={1}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
      </Stack>
    );
  }

  if (points.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }} spacing={1}>
        <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ height: 420, borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
      <MapContainer center={polandCenter} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.map((point) => (
          <Marker
            key={point.id}
            position={[point.lat, point.lon]}
            icon={point.kind === 'client_node' ? clientNodeIcon : sublocationIcon}
            eventHandlers={{
              click: () => onPointSelect?.(point),
            }}
          >
            <Popup>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{point.companyName}</Typography>
                <Typography variant="caption">{point.locationLabel}</Typography>
                <Typography variant="body2">{point.addressLine}</Typography>
                <Typography variant="body2">{point.postalCode} {point.city}</Typography>
                {point.cityCode ? <Typography variant="caption">Kod miejscowosci: {point.cityCode}</Typography> : null}
                {onPointNavigate ? (
                  <Typography variant="caption" color="primary.main" sx={{ cursor: 'pointer' }} onClick={() => onPointNavigate(point)}>
                    Pokaż stan klienta
                  </Typography>
                ) : null}
              </Stack>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <Paper
        variant="outlined"
        sx={{ position: 'absolute', right: 12, bottom: 24, zIndex: 1000, p: 1, bgcolor: 'rgba(255,255,255,0.96)' }}
      >
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2563eb' }} />
            <Typography variant="caption">{t('locations.map.markerSublocation')}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e11d48' }} />
            <Typography variant="caption">{t('locations.map.markerClientNode')}</Typography>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
